import { CONNECT_AUTH_URL } from '@src/global/App/constants/constants';
import { setCookieItem } from '@src/global/utils/cookie';
import { logError } from '@src/global/utils/log';
import { SiteConfig } from '@src/global/utils/SiteConfig';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  authorize,
  exchangeCodeForTokens,
  fetchOpenIdConfiguration,
  isConfig,
  OpenIdConfiguration,
  OpenIdConnectConfig,
  SiteSpecificConfigKeys,
} from 'wly-ecommerce-common';
import {
  CONNECT_ACCESS_TOKEN_KEY,
  CONNECT_ID_TOKEN_KEY,
  getTokensFromCookies,
} from './utils';

export const connectAuthentication = async (
  authorizationEndpoint: string,
  oidcConfig: OpenIdConnectConfig
) => {
  try {
    await authorize(authorizationEndpoint, oidcConfig);
  } catch (error) {
    logError(
      'connectAuthentication: Error during Connect authorization',
      error
    );
  }
};

export const fetchConnectTokens = async (
  code: string,
  state: string,
  tokenEndpoint: string
): Promise<boolean> => {
  try {
    const tokens = await exchangeCodeForTokens(tokenEndpoint, code, state);

    if (!tokens) {
      logError('fetchConnectTokens: No tokens received from token endpoint');
      return false;
    }

    const { access_token, id_token } = tokens;
    const oneHourFromNow = Date.now() / 1000 + 3600;
    setCookieItem(CONNECT_ACCESS_TOKEN_KEY, access_token, oneHourFromNow);
    setCookieItem(CONNECT_ID_TOKEN_KEY, id_token, oneHourFromNow);
    return true;
  } catch (error) {
    logError('fetchConnectTokens: Error fetching Connect tokens', error);
    return false;
  }
};

export const shouldTriggerConnectAuth = (): boolean => {
  const { siteId } = SiteConfig.getConfig();
  const { connectAccessToken, connectIdToken } = getTokensFromCookies();
  const hasConnectTokens = Boolean(connectAccessToken && connectIdToken);
  const isSiteWithConnectAuth = isConfig(
    SiteSpecificConfigKeys.connectAuthentication,
    siteId
  );
  return isSiteWithConnectAuth && !hasConnectTokens;
};

export const useOpenIdConnect = () => {
  const location = useLocation();
  const [openIdConfig, setOpenIdConfig] = useState<OpenIdConfiguration>();
  const [isOidcInitialized, setIsOidcInitialized] = useState<boolean>(false);
  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const oidcConfig = useMemo<OpenIdConnectConfig>(
    () => ({
      issuer: CONNECT_AUTH_URL,
      clientId: 'viax',
      redirectUri: `${window.location.origin}${location.pathname}`,
      scope: 'openid alm_identity_ids',
    }),
    [location.pathname]
  );

  useEffect(() => {
    if (!shouldTriggerConnectAuth()) return;

    if (!!openIdConfig) {
      if (!isOidcInitialized) setIsOidcInitialized(true);
      return;
    }

    try {
      fetchOpenIdConfiguration(CONNECT_AUTH_URL).then((config) => {
        setOpenIdConfig(config);
      });
    } catch (error) {
      logError('getOpenIdConfig: Error fetching OpenID configuration', error);
      return;
    }
  }, [openIdConfig]);

  const redirectToConnectForAuth = useCallback(async () => {
    if (!shouldTriggerConnectAuth() || !openIdConfig?.authorization_endpoint)
      return false;
    await connectAuthentication(
      openIdConfig.authorization_endpoint,
      oidcConfig
    );
    return true;
  }, [openIdConfig, oidcConfig]);

  const getConnectTokens = useCallback(async () => {
    if (!shouldTriggerConnectAuth() || !openIdConfig?.token_endpoint) return;

    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      logError(`fetchConnectTokens: Authorization error: ${error}`);
      return;
    }

    if (code && state) {
      const success = await fetchConnectTokens(
        code,
        state,
        openIdConfig.token_endpoint
      );
      if (success) {
        location.search = '';
      }
    }
  }, [openIdConfig, urlParams]);

  return {
    isOidcInitialized,
    redirectToConnectForAuth,
    getConnectTokens,
  };
};
